import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { Button } from '../common/index.js';
import logo from "../../static/images/logo.png";

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername');
    const savedPassword = localStorage.getItem('savedPassword');
    const rememberMeStatus = localStorage.getItem('rememberMe') === 'true';

    if (rememberMeStatus && savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (rememberMe) {
        localStorage.setItem('savedUsername', username);
        localStorage.setItem('savedPassword', password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.clear();
      }

      const response = await login(username, password);
      const role = response.data.role;

      await new Promise(resolve => setTimeout(resolve, 100));

      if (role === 'Accountant') navigate('/accountant-dashboard');
      else if (role === 'Agent') navigate('/agent-dashboard');
      else navigate('/admin-dashboard');

    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light flex items-center justify-center px-4 relative">

      <Link to="/" className="absolute top-6 left-6">
        <Button variant="primary" size="sm" icon={ArrowLeft}>
          Back to Home
        </Button>
      </Link>

      <div className="w-full max-w-5xl bg-white rounded-card shadow-equal grid grid-cols-1 lg:grid-cols-2">

        <div className="hidden lg:flex items-center justify-center bg-brand-light text-secondary relative overflow-hidden rounded-l-card">
          <div className="text-center px-12">
            <img
              src={logo}
              alt="Metro Logistics"
              className="mx-auto mb-8 max-w-[160px]"
            />
            <h2 className="text-2xl font-semibold leading-tight text-brand">
              Manage logistics with confidence
            </h2>
            <p className="mt-4 text-secondary text-sm">
              Secure access to operations and shipment management
            </p>
          </div>
        </div>

        <div className="p-8 sm:p-12">

          <h1 className="text-2xl font-semibold text-brand">
            Sign in to your account
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enter your credentials to continue
          </p>

          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 text-danger px-4 py-3 rounded-card text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">

            <div>
              <label className="text-sm font-medium text-secondary">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-clean mt-2"
                placeholder="Enter username"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-secondary">
                Password
              </label>
              <div className="relative mt-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-clean pr-12"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm hover:text-secondary"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-secondary">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 text-brand accent-brand"
                />
                Remember me
              </label>

              <Link to="/forgot-password" className="text-brand hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              Sign In
            </Button>
          </form>

        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm">
        <p className="text-md font-medium text-gray-500">Designed with Passion by <span className="text-brand">DigiQin</span></p>
      </div>

    </div>
  );
};

export default Login;
